import { OTSService } from '../services/OTSService'
import { ValidationUtils } from '../utils/ValidationUtils'

export class SecretForm {
  private onSecretCreated: (secretKey: string) => void
  private onError: (message: string) => void
  private otsService: OTSService

  constructor(onSecretCreated: (secretKey: string) => void, onError?: (message: string) => void) {
    this.onSecretCreated = onSecretCreated
    this.onError = onError || console.error
    this.otsService = new OTSService()
  }

  public render(): string {
    return `
      <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm rounded-2xl shadow-card border border-slate-100 dark:border-gray-700/90 p-8 sm:p-10 animate-slide-up">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-semibold font-sans tracking-tight text-slate-900 dark:text-white mb-2">Secure Sharing</h1>
          <p class="text-slate-600 dark:text-slate-300 max-w-md mx-auto leading-relaxed">
            Generate a protected link for sensitive data — <span class="text-primary-600 dark:text-primary-400 font-medium">read once</span>, then it's gone.
          </p>
        </div>

        <form id="secret-form" class="space-y-6" autocomplete="off">
          <div>
            <label for="secret-content" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secret Content *
            </label>
            <textarea
              id="secret-content"
              name="secret"
              rows="6"
              required
              class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none font-mono text-sm"
              placeholder="Enter the content you want to share securely..."
            ></textarea>
            <div class="mt-1 text-xs text-gray-500">
              Maximum 5000 characters. Content will be encrypted.
            </div>
          </div>

          <div>
            <label for="ttl" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Expiration Time
            </label>
            <select
              id="ttl"
              name="ttl"
              class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:border-primary-400"
            >
              <option value="300">5 minutes</option>
              <option value="900">15 minutes</option>
              <option value="1800">30 minutes</option>
              <option value="3600" selected>1 hour</option>
              <option value="14400">4 hours</option>
              <option value="86400">24 hours</option>
              <option value="259200">3 days</option>
              <option value="604800">7 days</option>
            </select>
            <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The link will expire automatically after the selected time.
            </div>
          </div>

          <div>
            <label for="passphrase" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Passphrase (Optional)
            </label>
            <div class="relative">
              <input
                type="password"
                id="passphrase"
                name="passphrase"
                class="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:border-primary-400"
                placeholder="Enter an additional passphrase..."
                autocomplete="off"
                data-form="false"
                data-lpignore="true"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 z-10 focus:outline-none"
                data-action="toggle-password"
                tabindex="-1"
              >
                <svg class="w-5 h-5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </button>
            </div>
            <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add an extra layer of security with a custom passphrase.
            </div>
          </div>

          <div class="bg-primary-50/90 dark:bg-primary-900/25 border border-primary-100 dark:border-primary-800/50 rounded-xl p-4">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              <div class="text-sm">
                <h4 class="font-medium text-primary-900 dark:text-primary-100 mb-1">Before sending</h4>
                <ul class="text-primary-800/90 dark:text-primary-200/90 space-y-1">
                  <li>• The generated link can only be accessed once</li>
                  <li>• After access, the content will be permanently deleted</li>
                  <li>• Share only through secure channels</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="flex space-x-4 pt-4">
            <button
              type="submit"
              class="flex-1 btn btn-primary"
              data-loading="Creating..."
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              Create Secure Link
            </button>

            <button
              type="button"
              class="btn btn-secondary"
              data-action="clear-form"
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    `
  }

  public bindEvents(container: HTMLElement): void {
    const form = container.querySelector('#secret-form') as HTMLFormElement
    const togglePasswordBtn = container.querySelector('[data-action="toggle-password"]') as HTMLButtonElement
    const clearBtn = container.querySelector('[data-action="clear-form"]') as HTMLButtonElement

    form?.addEventListener('submit', (e) => {
      e.preventDefault()
      e.stopImmediatePropagation()
      this.handleSubmit(e)
    })

    togglePasswordBtn?.addEventListener('click', this.togglePasswordVisibility.bind(this))

    clearBtn?.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      this.clearForm()
    })

    const textarea = container.querySelector('#secret-content') as HTMLTextAreaElement
    textarea?.addEventListener('input', this.updateCharacterCount.bind(this))
  }

  private async handleSubmit(_e: Event): Promise<void> {
    const secretTextarea = document.getElementById('secret-content') as HTMLTextAreaElement
    const ttlSelect = document.getElementById('ttl') as HTMLSelectElement
    const passphraseInput = document.getElementById('passphrase') as HTMLInputElement
    
    const secret = secretTextarea?.value || ''
    const ttl = parseInt(ttlSelect?.value || '3600')
    const passphrase = passphraseInput?.value || ''

    const validationResult = ValidationUtils.validateSecret(secret, passphrase)
    if (!validationResult.isValid) {
      this.showError(validationResult.errors.join(', '))
      return
    }

    const originalPassphrase = passphraseInput?.value || ''
    
    try {
      this.setLoading(true)
      
      if (passphraseInput) {
        passphraseInput.value = ''
      }
      
      const result = await this.otsService.createSecret({
        secret,
        ttl,
        passphrase: passphrase || undefined
      })

      this.onSecretCreated(result.secret_key)
      
      this.clearForm()
    } catch (error) {
      if (passphraseInput && originalPassphrase) {
        passphraseInput.value = originalPassphrase
      }
      this.showError(error instanceof Error ? error.message : 'Failed to create secret')
    } finally {
      this.setLoading(false)
    }
  }

  private togglePasswordVisibility(): void {
    const input = document.getElementById('passphrase') as HTMLInputElement
    if (input) {
      input.type = input.type === 'password' ? 'text' : 'password'
    }
  }

  private clearForm(): void {
    const form = document.getElementById('secret-form') as HTMLFormElement
    if (form) {
      const textarea = form.querySelector('#secret-content') as HTMLTextAreaElement
      const select = form.querySelector('#ttl') as HTMLSelectElement
      const passphrase = form.querySelector('#passphrase') as HTMLInputElement
      
      if (textarea) textarea.value = ''
      if (select) select.selectedIndex = 3
      if (passphrase) {
        passphrase.value = ''
        passphrase.type = 'password'
      }
      
      this.updateCharacterCount()
      
      const elements = form.querySelectorAll('.border-error-500')
      elements.forEach(el => el.classList.remove('border-error-500'))
      
      const errorTexts = form.querySelectorAll('.text-error-500')
      errorTexts.forEach(el => el.classList.remove('text-error-500'))
    }
  }

  private updateCharacterCount(): void {
    const textarea = document.getElementById('secret-content') as HTMLTextAreaElement
    if (textarea) {
      const count = textarea.value.length
      const maxLength = 5000
      
      let counter = document.querySelector('.character-counter')
      if (!counter) {
        counter = document.createElement('div')
        counter.className = 'character-counter text-xs text-gray-500 dark:text-gray-400 mt-1'
        textarea.parentNode?.appendChild(counter)
      }
      
      counter.textContent = `${count}/${maxLength} characters`
      
      if (count > maxLength) {
        counter.classList.add('text-error-500')
        textarea.classList.add('border-error-500')
      } else {
        counter.classList.remove('text-error-500')
        textarea.classList.remove('border-error-500')
      }
    }
  }

  private setLoading(loading: boolean): void {
    const submitBtn = document.querySelector('button[type="submit"]') as HTMLButtonElement
    if (submitBtn) {
      submitBtn.disabled = loading
      submitBtn.innerHTML = loading 
        ? submitBtn.dataset.loading || 'Loading...'
        : submitBtn.getAttribute('data-original') || submitBtn.innerHTML
      
      if (!loading && !submitBtn.getAttribute('data-original')) {
        submitBtn.setAttribute('data-original', submitBtn.innerHTML)
      }
    }
  }

  private showError(message: string): void {
    this.onError(message)
    console.error('SecretForm Error:', message)
  }
} 